import { ApiTypes } from '@/types/server/api.types';
import { logError } from '@/utils/logger.utils';
import { useNavigate } from 'react-router-dom';
import {
	LocalVideoTrack,
	ParticipantKind,
	Room,
	RoomEvent,
} from 'livekit-client';
import { useEffect, useRef, useState } from 'react';
import { isBrowserAndSystemValid } from '@/utils/browser.utils';
import { toast } from 'sonner';
import { Loader2, PhoneOff } from 'lucide-react';
import {
	RoomAudioRenderer,
	RoomContext,
	useVoiceAssistant,
	VideoTrack,
} from '@livekit/components-react';
import useLocalCameraTrack from '@/hooks/useLocalCameraTrack.hooks';
import useCombinedTranscriptions from '@/hooks/useCombinedTranscription.hooks';
import { brivioLogo } from '@/assets/svgs/BrivioLogo.svgs';
import BrivioLogo from '@/assets/pngs/BrivioLogo.png';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import posthog from 'posthog-js';

export const InterviewRoomPage = ({
	devices,
	handleStopAudioVideoStream,
	audioVideoStream,
	screenStream,
	setIsUserLoggedIn,
	userDetails,
	setScreenStream,
}: {
	devices: {
		speakerId: string | null;
		cameraId: string | null;
		microphoneId: string | null;
	} | null;
	handleStopAudioVideoStream: () => boolean;
	setIsUserLoggedIn: (isUserLoggedIn: boolean) => void;
	audioVideoStream: MediaStream | null;
	screenStream: MediaStream | null;
	userDetails: ApiTypes.TGetCandidateDetailsResponse['data'];
	setScreenStream: (screenStream: MediaStream | null) => void;
}) => {
	const navigate = useNavigate();

	// we are using state even though we are not setting it anywhere because we dont want it to re-render or refresh when the page re-renders. If we use ref or effect, it will re-render the page. Using just a simple variable will get re-initialized when page re-renders.
	const [room] = useState(
		new Room({
			videoCaptureDefaults: {
				frameRate: 24,
				resolution: {
					width: 1280,
					height: 720,
					frameRate: 24,
				},
			},
		})
	);

	const [isPageLoading, setIsPageLoading] = useState<boolean>(true);

	const disableRightClick = (event: MouseEvent) => {
		event.preventDefault();
	};

	const disableKeys = (event: KeyboardEvent) => {
		event.preventDefault();
	};

	// check browser and streams
	const performDeviceChecks = (): boolean => {
		if (
			!devices ||
			!devices.cameraId ||
			!devices.microphoneId ||
			!devices.speakerId
		) {
			posthog.capture('InterviewRoomPage_DeviceCheckFailed');
			toast('Device check failed', {
				description:
					'We are unable to access your camera, microphone or speaker. Please refresh the page and try again. Make sure you have allowed access to your camera, microphone and speaker.',
			});
			return false;
		}

		const browserCheck = isBrowserAndSystemValid();
		if (!browserCheck) {
			posthog.capture('InterviewRoomPage_BrowserCheckFailed');
			toast('System check failed', {
				description:
					'Organizations prefer interviews conducted from a desktop browser. Please join this call from a desktop device.',
			});
			return false;
		}

		if (!audioVideoStream) {
			posthog.capture('InterviewRoomPage_AudioVideoStreamFailed');
			toast('Audio or video failed', {
				description:
					'We are unable to access your camera or microphone. Please refresh the page and try again. Make sure you have allowed access to your camera and microphone.',
			});

			return false;
		}

		if (!screenStream) {
			posthog.capture('InterviewRoomPage_ScreenStreamFailed');
			toast('Screen share failed', {
				description:
					'We are unable to access your screen. Please refresh the page and try again. Make sure you have allowed access to entire screen.',
			});
			return false;
		}

		const videoTrack = screenStream?.getVideoTracks().length
			? new LocalVideoTrack(screenStream?.getVideoTracks()[0])
			: null;

		if (!videoTrack) {
			posthog.capture('InterviewRoomPage_ScreenStreamFailed');
			toast('Screen share failed', {
				description:
					'We are unable to access your screen. Please refresh the page and try again. Make sure you have allowed access to entire screen.',
			});
			return false;
		}

		return true;
	};

	const initializeInterview = async () => {
		try {
			if (!performDeviceChecks()) {
				navigate('/');
				return;
			}

			const roomAccessToken = await fetch(
				`${import.meta.env.VITE_SERVER_API_PREFIX}/interviews/livekit-token`,
				{
					method: 'GET',
					headers: {
						Authorization: `Bearer ${window.localStorage.getItem('token')}`,
					},
				}
			);

			const roomAccessTokenData =
				(await roomAccessToken.json()) as ApiTypes.TGetLiveKitTokenResponse;

			if (roomAccessTokenData.httpResponseCode !== 200) {
				if (roomAccessTokenData.httpResponseCode === 401) {
					window.localStorage.removeItem('token');
					setIsUserLoggedIn(false);
				}

				toast('Error while starting the interview', {
					description: roomAccessTokenData.message,
					duration: 10000,
				});

				navigate('/');
				return;
			}

			const roomsAccessToken = roomAccessTokenData.data?.accessToken;

			if (!roomsAccessToken) {
				throw new Error('Room access token not found in the API');
			}

			await room.connect(import.meta.env.VITE_LIVEKIT_URL, roomsAccessToken);

			await room.localParticipant.setMicrophoneEnabled(true);
			await room.localParticipant.setCameraEnabled(true, {
				resolution: {
					width: 1280,
					height: 720,
					frameRate: 24,
				},
			});

			const screenVideoTrack = screenStream?.getVideoTracks()[0];

			if (!screenVideoTrack) {
				throw new Error('Screen video track not found');
			}

			const livekitVideoTrack = new LocalVideoTrack(screenVideoTrack, {
				width: 1280,
				height: 720,
				frameRate: 24,
			});

			room.localParticipant.publishTrack(livekitVideoTrack);

			if (devices?.microphoneId) {
				room.switchActiveDevice('audioinput', devices.microphoneId);
			}
			if (devices?.cameraId) {
				room.switchActiveDevice('videoinput', devices.cameraId);
			}
			if (devices?.speakerId) {
				room.switchActiveDevice('audiooutput', devices.speakerId);
			}
		} catch (err) {
			logError(err, {
				candidateId: userDetails?.candidateId,
			});

			toast('Error while starting the interview', {
				description:
					'We are facing some issues while starting your interview. Please refresh the page and try again. If the error persists, please contact support.',
				duration: 10000,
			});

			navigate('/');
			return;
		} finally {
			setIsPageLoading(false);
		}
	};

	const handleStopAudioVideoStreamRef = useRef(handleStopAudioVideoStream);
	const setScreenStreamRef = useRef(setScreenStream);

	useEffect(() => {
		handleStopAudioVideoStreamRef.current = handleStopAudioVideoStream;
		setScreenStreamRef.current = setScreenStream;
	}, [handleStopAudioVideoStream, setScreenStream]);

	const isCallEndFlowStartedRef = useRef(false);

	const handleEndCall = async () => {
		toast.dismiss();

		if (isCallEndFlowStartedRef.current) {
			return;
		}

		isCallEndFlowStartedRef.current = true;

		try {
			// Explicitly disable LiveKit camera and microphone tracks before disconnecting
			await room.localParticipant.setCameraEnabled(false);
			await room.localParticipant.setMicrophoneEnabled(false);

			// Stop all published tracks
			room.localParticipant.trackPublications.forEach((trackPublication) => {
				if (trackPublication.track) {
					trackPublication.track.stop();
				}
			});
		} catch (error) {
			logError(error, {
				candidateId: userDetails?.candidateId,
			});
		}

		// Disconnect from LiveKit room
		room.disconnect(true);

		// Stop the original audio/video stream from parent component
		handleStopAudioVideoStream();

		// Stop screen stream tracks before setting to null
		if (screenStream) {
			screenStream.getTracks().forEach((track) => track.stop());
		}
		setScreenStream(null);

		toast('Interview ended', {
			description:
				'The interview has ended. If you feel this is an error, please contact support.',
			duration: 10000,
			id: 'interviewEndedToast',
		});

		navigate('/');

		return;
	};

	const detectDevTools = () => {
		setInterval(() => {
			const start = performance.now();
			// eslint-disable-next-line no-debugger
			debugger;
			const end = performance.now();
			if (end - start > 100) {
				toast.error(
					'We are detecting that you are trying to open dev tools, because of which we are ending the interview',
					{ id: 'devToolsDetectedToast', duration: 8000 }
				);

				handleEndCall();

				return;
			}
		}, 1000);
	};

	useEffect(() => {
		document.addEventListener('contextmenu', disableRightClick);
		document.addEventListener('keydown', disableKeys);

		initializeInterview();

		if (!import.meta.env.DEV) {
			detectDevTools();
		}

		return () => {
			document.removeEventListener('contextmenu', disableRightClick);
			document.removeEventListener('keydown', disableKeys);

			// Clean up all media tracks properly
			try {
				// Disable LiveKit tracks first
				room.localParticipant.setCameraEnabled(false);
				room.localParticipant.setMicrophoneEnabled(false);

				// Stop all published tracks
				room.localParticipant.trackPublications.forEach((trackPublication) => {
					if (trackPublication.track) {
						trackPublication.track.stop();
					}
				});
			} catch {
				// Ignore errors during cleanup
			}

			// Stop the original audio/video stream from parent component
			handleStopAudioVideoStreamRef.current();

			// Stop screen stream tracks before setting to null
			if (screenStream) {
				screenStream.getTracks().forEach((track) => track.stop());
			}
			setScreenStreamRef.current(null);

			// Disconnect from LiveKit room
			room.disconnect(true);
		};
	}, []);

	const onDeviceFailure = (error: Error) => {
		logError(error, {
			candidateId: userDetails?.candidateId,
		});

		toast('Device Error', {
			description:
				'We are unable to access your camera or microphone. Please contact support if you think this is a mistake.',
			duration: 10000,
		});

		handleEndCall();
	};

	useEffect(() => {
		if (room) {
			room.on(RoomEvent.MediaDevicesError, onDeviceFailure);
			room.on(RoomEvent.Disconnected, handleEndCall);

			return () => {
				room.off(RoomEvent.MediaDevicesError, onDeviceFailure);
				room.off(RoomEvent.Disconnected, handleEndCall);
			};
		}
	}, [room]);

	if (isPageLoading) {
		return (
			<div className="w-full min-h-screen bg-blue-50 p-8 flex items-center justify-center font-sans">
				<div className="flex flex-col items-center justify-center h-full">
					<Loader2 className="w-10 h-10 animate-spin text-blue-500" />
				</div>
			</div>
		);
	}

	return (
		<main
			data-lk-theme="default"
			className="h-full grid content-center bg-[var(--lk-bg)]"
		>
			<RoomContext.Provider value={room}>
				<div>
					<SimpleVoiceAssistant room={room} handleEndCall={handleEndCall} />
				</div>
			</RoomContext.Provider>
		</main>
	);
};

const SimpleVoiceAssistant = ({
	room,
	handleEndCall,
}: {
	room: Room;
	handleEndCall: () => void;
}) => {
	const cameraTrackRef = useLocalCameraTrack();
	const [isAssistantSpeaking, setIsAssistantSpeaking] = useState(false);
	const [isCandidateSpeaking, setIsCandidateSpeaking] = useState(false);
	const { state: agentState } = useVoiceAssistant();

	const combinedTranscriptions = useCombinedTranscriptions();
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (containerRef.current) {
			containerRef.current.scrollTop = containerRef.current.scrollHeight;
		}
	}, [combinedTranscriptions]);

	room.on('activeSpeakersChanged', (speakers) => {
		speakers.forEach((speaker) => {
			if (speaker.kind === ParticipantKind.AGENT) {
				setIsAssistantSpeaking(true);
				setIsCandidateSpeaking(false);
			} else {
				setIsCandidateSpeaking(true);
				setIsAssistantSpeaking(false);
			}
		});
	});

	return (
		<div className="w-full min-h-screen bg-[#242424] p-8 font-sans">
			<div className="flex items-center gap-1 cursor-pointer mb-8">
				<div className="w-5 h-5 text-[#7e7e7e]">{brivioLogo()}</div>
				<h1 className="text-2xl font-semibold text-[#7e7e7e] tracking-tight font-[GeneralSans-SemiBold]">
					brivio
				</h1>
			</div>
			<div className="flex flex-col items-center justify-center w-full gap-8">
				<div className="flex gap-4 w-[41rem]">
					<div className="flex flex-col gap-4 w-80">
						<Card
							className={cn(
								'w-80 h-[17.5rem] bg-[#202020] overflow-hidden relative border-none',
								isCandidateSpeaking && 'ring-2 ring-blue-500'
							)}
						>
							{/* For camera feed: */}
							{cameraTrackRef &&
							'publication' in cameraTrackRef &&
							cameraTrackRef.publication ? (
								<VideoTrack
									trackRef={cameraTrackRef}
									className="w-full h-full object-cover"
								/>
							) : null}
						</Card>

						<Card
							className={cn(
								'w-80 h-[17.5rem] bg-[#202020] flex justify-center items-center border-none',
								isAssistantSpeaking && 'ring-1 ring-blue-500'
							)}
						>
							<img
								src={BrivioLogo}
								alt="brivio logo"
								className="w-36 h-36 rounded-full border-8 border-[#282828]"
							/>
						</Card>
					</div>

					<Card className="w-80 h-[36rem] bg-[#202020] p-2 border-none">
						<div
							ref={containerRef}
							className="w-full h-full overflow-auto p-2 scroll-smooth scrollbar-none user-select-none text-amber-50 text-sm flex flex-col gap-2"
						>
							{combinedTranscriptions
								.filter((segment) => segment.role === 'assistant')
								.map((segment) => (
									<p
										id={segment.id}
										key={segment.id}
										className={cn(
											'text-base mb-4',
											segment.role === 'assistant'
												? 'text-[#f1f1f1]'
												: 'text-[#7e7e7e]'
										)}
									>
										{segment.text}
									</p>
								))}
						</div>
					</Card>
				</div>

				{agentState !== 'disconnected' && agentState !== 'connecting' && (
					<Button
						size="lg"
						className="flex items-center gap-2 px-6 py-2 bg-red-500 cursor-pointer rounded-full text-white hover:bg-red-600 transition-colors"
						onClick={() => {
							toast('End call?', {
								action: {
									label: 'Yes',
									onClick: () => {
										handleEndCall();
									},
								},
								description:
									"Are you sure you want to end the call? You won't be able to rejoin the call.",
								closeButton: true,
							});
						}}
					>
						<PhoneOff className="w-4 h-4" />
						<span className="w-16 font-medium text-center">End Call</span>
					</Button>
				)}

				<RoomAudioRenderer />
			</div>
		</div>
	);
};
